import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTasks } from '../database-tasks';
import { mapDbTaskToUiTask } from '../types';
import { DateRangeOption } from '../types/task-operations-types';
import { getDateRange } from '../operations/task-date-utils';
import { getTaskQueryKey } from '../operations/task-query-keys';

/**
 * Custom hook for fetching tasks with filterable date range
 */
export const useTasksQuery = (
  userId: string | undefined,
  dateRange: DateRangeOption,
  currentDate: Date,
  strictDateRange: boolean = false,
  noDateFilter: boolean = false
) => {
  const [error, setError] = useState<string | null>(null);
  
  // Calculate date range for filtering (skip if noDateFilter is true)
  const dateRangeParams = noDateFilter ? null : getDateRange(dateRange, currentDate);
  
  // Build query key - include noDateFilter to ensure proper caching
  const queryKey = getTaskQueryKey(userId, dateRange, dateRangeParams, strictDateRange, noDateFilter);
  
  // Query for fetching tasks
  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (!userId) return { active: [], completed: [] };
      
      try {
        const { data, error } = await fetchTasks(
          userId, 
          dateRangeParams ? { ...dateRangeParams, strictDateRange, noDateFilter } : { noDateFilter }
        );
        
        if (error) {
          setError(error.message);
          return { active: [], completed: [] };
        }
        
        if (!data) return { active: [], completed: [] };

        // Map DB tasks to UI tasks and separate them
        const mappedTasks = data.map(mapDbTaskToUiTask);
        
        // Filter tasks without changing their order
        // This ensures tasks don't reorder automatically when properties change
        const active = mappedTasks
          .filter(task => !task.isCompleted);
          
        const completed = mappedTasks
          .filter(task => task.isCompleted);
        
        return { active, completed };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        return { active: [], completed: [] };
      }
    },
    enabled: !!userId,
    // Improved caching strategy to prevent the "flash" of outdated content
    staleTime: 0, // Consider data stale immediately, forcing a refetch
    gcTime: 60000, // Keep inactive cache for 1 minute
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  return {
    ...queryResult,
    error
  };
};
